import { Component, ViewEncapsulation, OnInit, Input } from '@angular/core';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { ComponentCanDeactivate } from '../../../../app/shared/component-can-deactivate';
import { CarRepairBillingService } from '../../../../app/views/car-repair-billing/car-repair-billing.service';
import { ToastService } from '../../../../app/shared/toast.service';
import { ArInvoiceType } from '../../../../app/shared/arInvoiceType.model';
import { InvoiceService } from '../../../../app/views/invoice/invoice.service';

@Component({
	selector: 'email-send-to-intacct',
	//encapsulation: ViewEncapsulation.None,
	templateUrl: 'email-send-to-intacct.component.html'
})
export class EmailSendToIntacctComponent extends ComponentCanDeactivate implements OnInit {

	@Input() selectedInvoices: any[];
	@Input() billingPeriod: Date;
	@Input() invoiceType: any;
	sendingEmails: boolean;
	emailContactsLoading: boolean;
	contactType: string;
	hasCustomerContactError: boolean = false;
	service: any;
	sort: SortDescriptor[] = [{
		field: 'invoiceNumber',
		dir: 'asc'
	}];

	constructor(
		private toastr: ToastService,
		private carRepairBillingService: CarRepairBillingService,
		private invoiceService: InvoiceService) {
		super();
	}

	ngOnInit(): void {
		this.selectedInvoices = orderBy(this.selectedInvoices, this.sort);
		switch (this.invoiceType) {
			case ArInvoiceType.RepairCustomer:
			case ArInvoiceType.NoWork:
			case ArInvoiceType.BaseRentalCustomer:
			case ArInvoiceType.StipLoss:
			case ArInvoiceType.FmvDetermination:
			case ArInvoiceType.Scrap:
				this.contactType = 'Repair Billing';
				break;
			case ArInvoiceType.FreightSwitchCustomer:
				this.contactType = 'Switch Billing';
				break;
			case ArInvoiceType.ArExcessMileage:
			case ArInvoiceType.TankcarEqualization:
			case ArInvoiceType.CarHireMileageCustomer:
			case ArInvoiceType.CarHireMileageRailroad:
				this.contactType = 'Car Hire';
				break;
		}
		this.fetchingCustomerDetails();
	}

	fetchingCustomerDetails() {
		this.hasCustomerContactError = false;
		this.emailContactsLoading = true;
		this.invoiceService.getInvoiceContacts(this.selectedInvoices.map(x => x.id), this.invoiceType, this.contactType)
		.subscribe(result => {
			this.selectedInvoices.forEach(invoice => {
				const contacts = result[invoice.id];
				invoice.email = '';
				if(this.invoiceType == ArInvoiceType.Scrap)
				{
					if(!contacts){
						invoice.email = 'Warning: Missing Scrap Location contact';
					} else {
						invoice.email = contacts.map(x => x.email).join(', ');
					}

				} else if (!contacts || !contacts.find(x => x.contactType === this.contactType)) {
					invoice.email = 'Warning: Missing ' + this.contactType + ' contact type for customer'
				} else if (!contacts.find(x => x.contactType === 'Primary')) {
					invoice.email += 'Warning: Missing primary contact type for customer';
				} else {
					invoice.email = contacts.filter(x => x.contactType === this.contactType).map(x => x.email).join(', ');
				}
			});
			this.emailContactsLoading = false;
		},
		errorResult => {
			this.toastr.error(errorResult.error.Detail, 'Error!');
			this.emailContactsLoading = false;
			this.hasCustomerContactError = true;
		});
	}

	public rowCallback = (context: RowClassArgs) => {
		const isPosted = (!!context.dataItem.postedDate || this.hidePostedColumn);
		const isNotPosted = (!isPosted &&  this.isCarHireZeroValueRecord(context.dataItem) )
		return {
			'alert-success': isPosted ,
			'alert-danger': isNotPosted
		};
	}

	notReadyToSendEmails(): boolean {
		if (!this.selectedInvoices.some(i => i.isAnnualAr)) {
			return !!this.selectedInvoices.find(i => i.email?.includes('Warning'));
		}
		else {
			return !!this.selectedInvoices.find(i => !i.postedDate && (this.isCarHireZeroValueRecord(i))) || 
				!!this.selectedInvoices.find(i => i.email?.includes('Warning'));
		}
	}

	isCarHireZeroValueRecord(item): boolean {
		return this.invoiceType !== ArInvoiceType.CarHireMileageCustomer || (this.invoiceType === ArInvoiceType.CarHireMileageCustomer && item.creditTotal !== 0 && item.debitTotal !== 0);
	}

	public sendInvoices() {
		this.sendingEmails = true;
		const invoiceIds = this.selectedInvoices.map(x => x.id);
		this.emailContactsLoading = true;
		this.carRepairBillingService.sendInvoices(invoiceIds, this.billingPeriod,this.invoiceType)
			.subscribe(() => {
				this.sendingEmails = false;
				this.toastr.success('You will be notified when they are all sent.', 'Emails queued for processing');
				this.emailContactsLoading = false;
			},
			errorResult => {
				this.toastr.error(errorResult.error.Detail, 'Error occurred while sending emails!');
				this.emailContactsLoading = false;
			});
	}

	sortChange(event: SortDescriptor[]) {
		this.sort = event;
		this.selectedInvoices = orderBy(this.selectedInvoices, this.sort);
	}

	get hideLeaseColumn() : boolean
	{
		let hideLease: boolean  = false;
		switch (this.invoiceType) {
			case ArInvoiceType.Scrap:
			case ArInvoiceType.BaseRentalCustomer:	
			case ArInvoiceType.ArExcessMileage:
			case ArInvoiceType.TankcarEqualization:
				hideLease = true;
				break;

			default:
				hideLease = false;
				break;
		}
		return hideLease;
	}
	get hideCustomerColumn() : boolean
	{
		let hideCustomer: boolean  = false;
		switch (this.invoiceType) {
			case ArInvoiceType.Scrap:
				hideCustomer = true;
				break;

			default:
				hideCustomer = false;
				break;
		}
		return hideCustomer;
	}
	get hideInvoiceColumn() : boolean
	{
		let hideInvoiceNumber: boolean  = false;
		switch (this.invoiceType) {
			case ArInvoiceType.BaseRentalCustomer:
				hideInvoiceNumber = true;
				break;

			default:
				hideInvoiceNumber = false;
				break;
		}
		return hideInvoiceNumber;
	}
	get hidePostedColumn() : boolean
	{
		let hidePosted: boolean  = false;
		switch (this.invoiceType) {
			case ArInvoiceType.TankcarEqualization:
				if (this.selectedInvoices.some(i => !i.isTeAnnualAr)) {
					hidePosted = true;
				}
				break;

			default:
				hidePosted = false;
				break;
		}
		return hidePosted;
	}
	get customerColumnTitle()
	{
		let title = "Customer"; 
		switch (this.invoiceType) {
			case ArInvoiceType.Scrap:
				title = "Scrap Location";
				break;

			default:
				title = "Customer";
				break;
		}
		return title;
	}
	get invoiceColumnTitle()
	{
		let title = "Invoice #"; 
		switch (this.invoiceType) {
			case ArInvoiceType.TankcarEqualization:
				title = this.selectedInvoices.some(i => !i.isTeAnnualAr) ? "Statement #" : "Invoice #";
				break;

			default:
				title = "Invoice #";
				break;
		}
		return title;
	}
}
